import { useEffect, useState } from "react";
import { Eye, EyeOff, LockKeyhole, PackageSearch, ShieldCheck, UserRound } from "lucide-react";
import { loadAdminTradeLedger, loadCustomerOrders, loginAdmin, loginCustomer } from "./api";
import { TradeLedgerPanel } from "./TradeLedger";
import { LoadingOverlay } from "./LoadingOverlay";
import { PullToRefresh } from "./PullToRefresh";
import type { CustomerOrder } from "./types";

const SESSION_TOKEN_KEY = "yoyo-access-session-token";
const SESSION_ROLE_KEY = "yoyo-access-session-role";
const ADMIN_PERSISTED_SESSION_KEY = "yoyo-admin-persisted-session";
const ADMIN_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
type AccessRole = "admin" | "customer";

type PersistedAdminSession = {
  accessToken: string;
  role: "admin";
  expiresAt: number;
};

function readPersistedAdminSession(): PersistedAdminSession | null {
  const raw = localStorage.getItem(ADMIN_PERSISTED_SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as Partial<PersistedAdminSession>;
    if (
      session.role !== "admin" ||
      typeof session.accessToken !== "string" ||
      !session.accessToken ||
      typeof session.expiresAt !== "number" ||
      session.expiresAt <= Date.now()
    ) {
      localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
      return null;
    }
    return session as PersistedAdminSession;
  } catch {
    localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
    return null;
  }
}

function getInitialAccessSession(): { token: string; role: AccessRole | null } {
  const persistedAdmin = readPersistedAdminSession();
  if (persistedAdmin) {
    return { token: persistedAdmin.accessToken, role: persistedAdmin.role as AccessRole };
  }
  const legacyToken = sessionStorage.getItem("yoyo-admin-session-token") ?? "";
  const token = sessionStorage.getItem(SESSION_TOKEN_KEY) ?? legacyToken;
  const savedRole = sessionStorage.getItem(SESSION_ROLE_KEY);
  const role: AccessRole | null = savedRole === "customer" || savedRole === "admin" ? savedRole : legacyToken ? "admin" : null;
  return { token, role };
}

export function clearAdminSession() {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_ROLE_KEY);
  sessionStorage.removeItem("yoyo-admin-session-token");
  localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
  localStorage.removeItem("yoyo-admin-token");
}

export function AdminPortal() {
  const [initialSession] = useState(getInitialAccessSession);
  const [token, setToken] = useState(initialSession.token);
  const [role, setRole] = useState<AccessRole | null>(initialSession.role);
  const [mode, setMode] = useState<AccessRole>("customer");
  const [showPassword, setShowPassword] = useState(false);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [status, setStatus] = useState<"locked" | "checking" | "ready" | "error">(
    token && role ? "checking" : "locked"
  );

  useEffect(() => {
    if (!token || !role) return;
    let cancelled = false;
    setStatus("checking");
    const request = role === "admin" ? loadAdminTradeLedger(token, 1) : loadCustomerOrders(token);
    void request.then((result) => {
      if (cancelled) return;
      if (role === "customer") setOrders(result as CustomerOrder[]);
      setStatus("ready");
    }).catch(() => {
      if (cancelled) return;
      clearAdminSession();
      setToken("");
      setRole(null);
      setStatus("error");
    });
    return () => { cancelled = true; };
  }, [role, token]);

  const isChecking: boolean = status === "checking";

  async function signIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("checking");
    try {
      const session = mode === "admin"
        ? await loginAdmin(String(formData.get("username") ?? ""), String(formData.get("password") ?? ""))
        : await loginCustomer(String(formData.get("phone") ?? ""), Number(formData.get("orderId")));
      if (session.role === "admin") {
        localStorage.setItem(ADMIN_PERSISTED_SESSION_KEY, JSON.stringify({
          accessToken: session.accessToken,
          role: session.role,
          expiresAt: Date.now() + ADMIN_SESSION_TTL_MS
        } satisfies PersistedAdminSession));
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_ROLE_KEY);
      } else {
        sessionStorage.setItem(SESSION_TOKEN_KEY, session.accessToken);
        sessionStorage.setItem(SESSION_ROLE_KEY, session.role);
      }
      sessionStorage.removeItem("yoyo-admin-session-token");
      localStorage.removeItem("yoyo-admin-token");
      setToken(session.accessToken);
      setRole(session.role);
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  async function refreshCustomerOrders() {
    if (!token || role !== "customer") {
      return;
    }
    const nextOrders = await loadCustomerOrders(token);
    setOrders(nextOrders);
  }

  if (status === "ready" && token && role === "admin") {
    return <><LoadingOverlay active={false} /><TradeLedgerPanel token={token} onChanged={() => undefined} /></>;
  }
  if (status === "ready" && token && role === "customer") {
    return <><LoadingOverlay active={false} /><PullToRefresh onRefresh={refreshCustomerOrders}><CustomerOrders orders={orders} /></PullToRefresh></>;
  }

  return (
    <>
      <LoadingOverlay active={isChecking} label={token && role ? "正在恢复会话" : "正在验证"} />
      {!isChecking && <section className="admin-login account-login" aria-labelledby="access-login-title">
      <div className="admin-login-mark" aria-hidden="true">
        {mode === "admin" ? <LockKeyhole size={24} /> : <PackageSearch size={24} />}
      </div>
      <h2 id="access-login-title">{mode === "admin" ? "管理员登录" : "查询我的订单"}</h2>
      <p>{mode === "admin" ? "登录后进入采销与分析。" : "手机号和订单号需与下单信息一致。"}</p>
      <div className="access-role-switch" role="tablist" aria-label="登录身份">
        <button type="button" role="tab" aria-selected={mode === "customer"} className={mode === "customer" ? "active" : ""} onClick={() => { setMode("customer"); setStatus("locked"); }}>
          <UserRound size={16} />我的订单
        </button>
        <button type="button" role="tab" aria-selected={mode === "admin"} className={mode === "admin" ? "active" : ""} onClick={() => { setMode("admin"); setStatus("locked"); }}>
          <LockKeyhole size={16} />管理员
        </button>
      </div>
      <form key={mode} onSubmit={signIn}>
        {mode === "customer" ? <>
          <label><span>下单手机号</span><input name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 位手机号" pattern="1[3-9][0-9]{9}" required /></label>
          <label><span>订单号</span><input name="orderId" type="number" inputMode="numeric" placeholder="订单编号" min="1" required /></label>
        </> : <>
          <label><span>管理员账号</span><input name="username" autoComplete="username" placeholder="输入账号" required /></label>
          <label>
            <span>密码</span>
            <div className="admin-secret-field">
              <input name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="输入密码" required />
              <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "隐藏密码" : "显示密码"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
          </label>
        </>}
        <button className="primary-button" type="submit" disabled={status === "checking"}>
          {status === "checking" ? "正在验证" : mode === "admin" ? "登录管理台" : "验证并查询"}
        </button>
      </form>
      {status === "error" && <p className="admin-login-error" role="alert">{mode === "admin" ? "账号或密码不正确。" : "手机号或订单号不匹配。"}</p>}
      <small><ShieldCheck size={14} />会话仅保存在当前浏览器，关闭后需重新验证</small>
      </section>}
    </>
  );
}

function CustomerOrders({ orders }: { orders: CustomerOrder[] }) {
  return (
    <section className="customer-orders" aria-labelledby="customer-orders-title">
      <header><h2 id="customer-orders-title">我的订单</h2><span>{orders.length} 笔</span></header>
      <div className="customer-order-list">
        {orders.map((order) => (
          <article key={order.id}>
            {order.product_image ? <img src={order.product_image} alt="" /> : <div className="customer-order-placeholder"><PackageSearch size={20} /></div>}
            <div><small>订单 #{order.id} · {formatDate(order.created_at)}</small><strong>{order.product_title || order.product_id}</strong><span>{order.city} · {translateIntent(order.intent)}</span></div>
            <b className={`customer-order-status ${order.status}`}>{translateOrderStatus(order.status)}</b>
          </article>
        ))}
      </div>
    </section>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function translateIntent(value: CustomerOrder["intent"]) {
  return { reserve: "预约看货", buy: "购买", rent: "先租后买" }[value];
}

function translateOrderStatus(value: CustomerOrder["status"]) {
  return { new: "待确认", confirmed: "已确认", paid: "已付款", fulfilled: "已完成", cancelled: "已取消" }[value];
}
